import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment/moment';
import lodashCompact from 'lodash/fp/compact';

import { DATA_TABLE_SOURCE_UTILITY_PARTNER_ADDRESSES } from 'common/dataTableConstants';

import {
  PARTNER_ADDRESS_STATUS_LISTED,
  PARTNER_ADDRESS_STATUS_UNLISTED,
  PARTNER_ADDRESS_STATUS_WITHDRAWN,
  PARTNER_ADDRESS_STATUS_LEASED,
  UTILITY_TYPE_ID_LIABILITY_INSURANCE,
  PARTNER_ADDRESS_STATUS_PENDING_VERIFICATION,
} from 'common/utility/constants';

import { makeAddressCityStateString } from '@utilityprofit/address-helpers';
import makeFullName from 'common/util/makeFullName';
import dateStringMomentWithCurrentTime from 'common/util/dateStringMomentWithCurrentTime';

import {
  SR_API_ACTION_ADDRESS_GET_BUILDING_UNITS_PREVIEW,
  SR_API_ACTION_ADDRESS_UPDATE_REQUIRED_TYPES,
  SR_API_ACTION_ADDRESS_UPDATE_BUILDING_UNIT_REQUIRED_TYPES,
  SR_API_ACTION_ADDRESS_START_REMINDERS_FOR_RENTER,
  SR_API_ACTION_UTILITY_ACTIVATION_GET,
  SR_API_ACTION_UTILITY_PARTNER_ADDRESS_UPDATE_ADDRESS,
  SR_API_ACTION_INSURANCE_BACKFILL_STATUS,
} from 'api/utilityActionTypes';

import requestUtilityApi from 'client/actions/requestUtilityApi';
import useDispatch from 'client/hooks/useDispatch';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrDataTableUI from 'client/components/SrDataTableUI';
import SrTooltip, { SR_TOOLTIP_POSITION_CENTER } from 'client/components/SrTooltip';
import { SR_TABLE_THEME_PARTNER_CLEAR } from 'client/components/SrTable';
import SrDataTableConnected from 'client/components/SrDataTableConnected';
import SrUtilityProfitNewAddressModal from 'client/components/SrUtilityProfit/SrUtilityProfitNewAddressModal';
import SrUtilityAddressRequiredTypesModal from 'client/components/SrUtilityAddressRequiredTypesModal/SrUtilityAddressRequiredTypesModal';
import SrUtilityBuildingUnitsConfirmationModal from 'client/components/SrUtilityBuildingUnitsConfirmationModal/SrUtilityBuildingUnitsConfirmationModal';
import SrUtilityProfitRenterContactInformationModal from 'client/components/SrUtilityProfit/SrUtilityProfitRenterContactInformationModal';
import SrUtilityProfitAddressesDataTableHeader from 'client/components/SrUtilityProfitAddressesDataTable/SrUtilityProfitAddressesDataTableHeader';
import {
  canDismissBuildingUnitsConfirmation,
  getNextExcludedSiblingAddressIds,
  getNextBuildingUnitsPreviewInstanceId,
  handleBuildingUnitsApplyError,
  isLatestBuildingUnitsPreviewRequest,
  makeBuildingUnitsPreviewFailureState,
  makeBuildingUnitsPreviewLoadingState,
  makeBuildingUnitsPreviewQueryId,
  makeBuildingUnitsPreviewSuccessState,
  makeBuildingUnitsUpdatePayload,
  shouldRequestBuildingUnitsPreview,
} from 'client/components/SrUtilityProfitAddressesDataTable/buildingUnits.helpers';
import SrUtilityConfigurationSourceMarker from 'client/components/SrUtilityConfigurationSourceMarker/SrUtilityConfigurationSourceMarker';
import SrSimpleMessageModal, {
  SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP,
} from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';
import {
  SR_BUTTON_THEME_ADOBE_GREY,
  SR_BUTTON_THEME_ADOBE_YELLOW,
} from 'client/components/SrButton/SrButton';
import { isHomebuyerPartner } from 'common/utility/helpers';

const BACKFILL_STORAGE_KEY = (partnerId) => `up_liability_backfill_${partnerId}`;
// BACKFILL_TTL_MS: how long a localStorage entry survives page reloads (resume window).
// BACKFILL_TIMEOUT_MS: max polling duration per session. TTL > timeout so a reload within
// 15 min resumes polling for up to 5 more minutes.
const BACKFILL_TTL_MS = 15 * 60 * 1000;
const BACKFILL_TIMEOUT_MS = 5 * 60 * 1000;
const POLL_INTERVALS_MS = [3000, 6000, 12000, 24000, 30000];

function getDataTableSettingsFromProps({ utilityPartnerId }) {
  const filter = {
    status: [
      PARTNER_ADDRESS_STATUS_LISTED,
      PARTNER_ADDRESS_STATUS_UNLISTED,
      PARTNER_ADDRESS_STATUS_WITHDRAWN,
      PARTNER_ADDRESS_STATUS_LEASED,
      PARTNER_ADDRESS_STATUS_PENDING_VERIFICATION,
    ],
  };

  if (utilityPartnerId) {
    filter.utilityPartnerId = utilityPartnerId;
  }

  const viewId = `${DATA_TABLE_SOURCE_UTILITY_PARTNER_ADDRESSES}_${
    utilityPartnerId || 'no_partner_id'
  }`;

  const dataTableSettings = {
    dataSource: DATA_TABLE_SOURCE_UTILITY_PARTNER_ADDRESSES,
    rootViewId: viewId,
    initialViewSettings: {
      filter,
      sort: {
        createdAt: 'desc',
      },
    },
    count: 8,
    itemsPropName: 'addresses',
    resetOnUnmount: true,
  };
  return dataTableSettings;
}

const SrUtilityProfitAddressDataTableAddressCell = ({ address, showPartner }) => (
  <SrText font="greycliff" fontSize="16px" weight="400">
    <SrText weight="800">{address.streetDisplay}</SrText>
    {`  ${makeAddressCityStateString(address)}`}
    {showPartner && (
      <SrBox marginTop="-7px">
        <SrText size="12" weight="500">
          {address.partnerName}
        </SrText>
      </SrBox>
    )}
  </SrText>
);

SrUtilityProfitAddressDataTableAddressCell.propTypes = {
  address: PropTypes.object.isRequired,
};

SrUtilityProfitAddressDataTableAddressCell.displayName =
  'SrUtilityProfitAddressDataTableAddressCell';

const formatDate = (date) => {
  return moment(date).format('MMM D, YYYY');
};

const SrUtilityProfitAddressesDataTable = ({
  title,
  showPartner,
  utilityPartner,
  utilityPartnerSlug,
  utilityPartnerList,
  utilityTypes,
  tenantLiabilityEnabled,
  backfillTriggeredAt,
}) => {
  const utilityPartnerId = utilityPartner?.id;
  const isHomebuyer = isHomebuyerPartner(utilityPartner);

  // Filter out liability insurance unless the partner offers it (SimpleCover or an
  // exclusive partner referral link)
  const filteredUtilityTypes = utilityTypes.filter(
    (type) => type.id !== UTILITY_TYPE_ID_LIABILITY_INSURANCE || tenantLiabilityEnabled,
  );

  const [updatingAddressId, setUpdatingAddressId] = useState(null);
  const [newAddressModalIsOpen, setNewAddressModalIsOpen] = useState(false);
  const [selectingAddress, setSelectingAddress] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [renterContactInfoPendingConfirmation, setRenterContactInfoPendingConfirmation] = useState(
    null,
  );
  const [buildingUnitsConfirmation, setBuildingUnitsConfirmation] = useState(null);
  const [buildingUnitsPreviewInstanceId] = useState(getNextBuildingUnitsPreviewInstanceId);
  const [buildingUnitsAreUpdating, setBuildingUnitsAreUpdating] = useState(false);
  const [buildingUnitsUpdateError, setBuildingUnitsUpdateError] = useState(null);
  const dispatch = useDispatch();

  const startPollingRef = useRef(null);
  const abortRef = useRef(false);
  const isPollingRef = useRef(false);
  // Always holds the latest reloadItems from the render prop so the polling
  // closure never calls a stale version captured at polling-start time.
  const reloadItemsRef = useRef(null);
  const buildingUnitsPreviewRequestIdRef = useRef(0);
  const buildingUnitsReturnFocusRef = useRef(null);

  useEffect(() => {
    setBuildingUnitsConfirmation(null);
    setBuildingUnitsAreUpdating(false);
    setBuildingUnitsUpdateError(null);
    buildingUnitsReturnFocusRef.current = null;

    return () => {
      buildingUnitsPreviewRequestIdRef.current += 1;
    };
  }, [utilityPartnerId]);

  const requestBuildingUnitsPreview = (previewContext, { successMessage = null } = {}) => {
    const requestId = buildingUnitsPreviewRequestIdRef.current + 1;
    buildingUnitsPreviewRequestIdRef.current = requestId;
    setBuildingUnitsAreUpdating(true);
    setBuildingUnitsUpdateError(null);
    setBuildingUnitsConfirmation(makeBuildingUnitsPreviewLoadingState(previewContext));

    return dispatch(requestUtilityApi, {
      action: SR_API_ACTION_ADDRESS_GET_BUILDING_UNITS_PREVIEW,
      queryId: makeBuildingUnitsPreviewQueryId({
        addressId: previewContext.addressId,
        instanceId: buildingUnitsPreviewInstanceId,
        requestId,
      }),
      payload: {
        addressId: previewContext.addressId,
        utilityPartnerId: previewContext.utilityPartnerId,
      },
      onSuccess: ({ buildingDisplay, reviewSource, siblingUnits = [] }) => {
        if (
          !isLatestBuildingUnitsPreviewRequest({
            completedRequestId: requestId,
            latestRequestId: buildingUnitsPreviewRequestIdRef.current,
          })
        ) {
          return;
        }

        setBuildingUnitsAreUpdating(false);
        const confirmation = makeBuildingUnitsPreviewSuccessState(previewContext, {
          buildingDisplay,
          reviewSource,
          siblingUnits,
        });
        setBuildingUnitsConfirmation(confirmation);
        setBuildingUnitsUpdateError(confirmation ? successMessage : null);
        if (!confirmation) {
          if (
            buildingUnitsReturnFocusRef.current?.isConnected &&
            typeof buildingUnitsReturnFocusRef.current.focus === 'function'
          ) {
            buildingUnitsReturnFocusRef.current.focus();
          }
        }
      },
      onError: () => {
        if (
          !isLatestBuildingUnitsPreviewRequest({
            completedRequestId: requestId,
            latestRequestId: buildingUnitsPreviewRequestIdRef.current,
          })
        ) {
          return;
        }

        setBuildingUnitsAreUpdating(false);
        setBuildingUnitsConfirmation(makeBuildingUnitsPreviewFailureState(previewContext));
      },
    });
  };

  const closeBuildingUnitsConfirmation = () => {
    if (
      !canDismissBuildingUnitsConfirmation({
        isApplying: buildingUnitsAreUpdating,
        previewIsLoading: buildingUnitsConfirmation?.previewIsLoading,
      })
    ) {
      return;
    }
    buildingUnitsPreviewRequestIdRef.current += 1;
    setBuildingUnitsConfirmation(null);
    setBuildingUnitsAreUpdating(false);
    setBuildingUnitsUpdateError(null);
  };

  const toggleBuildingUnitSelection = (addressId) => {
    if (buildingUnitsAreUpdating) {
      return;
    }

    setBuildingUnitsConfirmation((confirmation) => {
      if (!confirmation) {
        return confirmation;
      }

      return {
        ...confirmation,
        excludedSiblingAddressIds: getNextExcludedSiblingAddressIds({
          addressId,
          excludedSiblingAddressIds: confirmation.excludedSiblingAddressIds,
        }),
      };
    });
  };

  const applyBuildingUnitSettings = () => {
    if (!buildingUnitsConfirmation || buildingUnitsAreUpdating) {
      return;
    }

    if (buildingUnitsConfirmation.previewFailed) {
      return requestBuildingUnitsPreview(buildingUnitsConfirmation);
    }

    const payload = makeBuildingUnitsUpdatePayload(buildingUnitsConfirmation);
    if (!payload) {
      return undefined;
    }

    setBuildingUnitsAreUpdating(true);
    setBuildingUnitsUpdateError(null);
    return dispatch(requestUtilityApi, {
      action: SR_API_ACTION_ADDRESS_UPDATE_BUILDING_UNIT_REQUIRED_TYPES,
      payload,
      onSuccess: () => {
        setBuildingUnitsConfirmation(null);
        setBuildingUnitsAreUpdating(false);
        if (reloadItemsRef.current) {
          Promise.resolve(reloadItemsRef.current()).catch(() => null);
        }
      },
      onError: (error) => {
        return handleBuildingUnitsApplyError({
          error,
          previewContext: buildingUnitsConfirmation,
          requestPreview: requestBuildingUnitsPreview,
          setIsUpdating: setBuildingUnitsAreUpdating,
          setUpdateError: setBuildingUnitsUpdateError,
        });
      },
    });
  };

  useEffect(() => {
    if (!backfillTriggeredAt || !utilityPartnerId) return;
    localStorage.setItem(
      BACKFILL_STORAGE_KEY(utilityPartnerId),
      JSON.stringify({ startedAt: backfillTriggeredAt }),
    );
    if (startPollingRef.current) { startPollingRef.current(); }
  }, [backfillTriggeredAt, utilityPartnerId]);

  useEffect(() => {
    if (!utilityPartnerId) return;
    const raw = localStorage.getItem(BACKFILL_STORAGE_KEY(utilityPartnerId));
    if (!raw) return;
    let startedAt;
    try {
      ({ startedAt } = JSON.parse(raw));
    } catch {
      localStorage.removeItem(BACKFILL_STORAGE_KEY(utilityPartnerId));
      return;
    }
    if (Date.now() - startedAt > BACKFILL_TTL_MS) {
      localStorage.removeItem(BACKFILL_STORAGE_KEY(utilityPartnerId));
      return;
    }
    // Defer so the SrDataTableConnected render prop has run and populated startPollingRef.
    setTimeout(() => startPollingRef.current?.(), 0);
  }, [utilityPartnerId]);

  useEffect(() => {
    return () => {
      abortRef.current = true;
    };
  }, []);

  const dataTableHeaders = [
    {
      content: 'Address',
      dataTableFieldId: 'streetDisplay',
      sortPosition: 'right',
      style: { width: '300px' },
    },
    {
      content: 'Created',
      dataTableFieldId: 'createdAt',
      sortPosition: 'right',
    },
    {
      content: (
        <SrText weight="800">
          Utilities&nbsp;
          <SrText weight="400">
            (Enabled/<SrText color="orange300">Disabled</SrText>)
          </SrText>
        </SrText>
      ),
    },
    {
      content: 'Set By',
      style: { width: '112px' },
    },
    {
      content: '',
    },
    {
      content: '',
    },
  ];

  const onSearchInputChange = ({ loadNextPage, viewSettings, input }) => {
    const searchTerm = input.target.value;
    setSearchInput(searchTerm);
    if (viewSettings) {
      viewSettings.update({
        ...viewSettings,
        loadAllData: true,
        fuzzy: !searchTerm
          ? null
          : {
              queryString: searchTerm,
              columns: ['display'],
            },
        count: 50,
      });
      loadNextPage();
    }
  };

  return (
    <SrDataTableConnected {...getDataTableSettingsFromProps({ utilityPartnerId })}>
      {({
        addresses,
        reloadItems,
        viewSettings,
        dataTableApiState,
        loadNextPage,
        dataTableIsFullyLoaded,
      }) => {
        // eslint-disable-line max-len
        reloadItemsRef.current = () => reloadItems({ count: Math.max(addresses.length, 8) });

        startPollingRef.current = () => {
          if (isPollingRef.current) return;
          isPollingRef.current = true;
          let tickIndex = 0;
          const pollingStartedAt = Date.now();

          function tick() {
            if (abortRef.current) return;
            if (Date.now() - pollingStartedAt > BACKFILL_TIMEOUT_MS) {
              localStorage.removeItem(BACKFILL_STORAGE_KEY(utilityPartnerId));
              isPollingRef.current = false;
              return;
            }

            dispatch(requestUtilityApi, {
              action: SR_API_ACTION_INSURANCE_BACKFILL_STATUS,
              payload: { utilityPartnerId },
              onSuccess: ({ status }) => {
                if (abortRef.current) return;
                if (reloadItemsRef.current) { reloadItemsRef.current(); }
                if (status === 'completed') {
                  localStorage.removeItem(BACKFILL_STORAGE_KEY(utilityPartnerId));
                  isPollingRef.current = false;
                  return;
                }
                const delay = POLL_INTERVALS_MS[Math.min(tickIndex, POLL_INTERVALS_MS.length - 1)];
                tickIndex += 1;
                setTimeout(tick, delay);
              },
              onError: () => {
                localStorage.removeItem(BACKFILL_STORAGE_KEY(utilityPartnerId));
                isPollingRef.current = false;
              },
            });
          }

          tick();
        };

        const updatingAddress = !updatingAddressId
          ? null
          : addresses.find(({ id }) => id === updatingAddressId);

        if (dataTableApiState.inProgress && addresses.length === 0) {
          return (
            <SrBox paddingBottom="40px">
              <SrUtilityProfitAddressesDataTableHeader
                title={title}
                searchInput={searchInput}
                onSearchInputChange={(input) =>
                  onSearchInputChange({ loadNextPage, viewSettings, input })
                }
                onAddAddress={() => setNewAddressModalIsOpen(true)}
              />
              <SrBox marginTop="20px" paddingBottom="20px">
                <div className="loading-ellipses clearfix" />
              </SrBox>
            </SrBox>
          );
        }

        const reloadByAddressId = (addressId) => {
          return reloadItems({
            count: 1,
            filter: {
              id: addressId,
            },
          });
        };

        const updateRequiredTypes = ({ utilityTypeIds, propertyManagerEmail, markAsReviewed }) => {
          return dispatch(requestUtilityApi, {
            action: SR_API_ACTION_UTILITY_PARTNER_ADDRESS_UPDATE_ADDRESS,
            payload: {
              id: updatingAddress.partnerAddressId,
              updates: {
                propertyManagerEmail: propertyManagerEmail || null,
              },
            },
            onError(error) {
              global.alert(error.message);
            },
            onSuccess: () => {
              const buildingUnitsPreviewIsRequired = shouldRequestBuildingUnitsPreview({
                currentUtilityTypeIds: updatingAddress.requiredTypes.map(({ id }) => id),
                markAsReviewed,
                utilityTypeIds,
              });

              if (!buildingUnitsPreviewIsRequired) {
                setUpdatingAddressId(null);
                return reloadByAddressId(updatingAddress.id);
              }
              return dispatch(requestUtilityApi, {
                action: SR_API_ACTION_ADDRESS_UPDATE_REQUIRED_TYPES,
                payload: {
                  addressId: updatingAddress.id,
                  utilityTypeIds,
                  utilityPartnerId: updatingAddress.utilityPartnerId,
                  markAsReviewed,
                },
                onError(error) {
                  global.alert(error.message);
                },
                onSuccess: () => {
                  const savedAddress = updatingAddress;
                  setUpdatingAddressId(null);
                  setBuildingUnitsUpdateError(null);
                  setBuildingUnitsAreUpdating(false);

                  Promise.resolve(reloadByAddressId(savedAddress.id)).catch(() => null);
                  requestBuildingUnitsPreview({
                    addressId: savedAddress.id,
                    sourceUnit: {
                      addressDisplay: savedAddress.display,
                      addressId: savedAddress.id,
                      previousUtilityTypeIds: savedAddress.requiredTypes.map(({ id }) => id),
                      utilityTypeIds,
                    },
                    utilityPartnerId: savedAddress.utilityPartnerId,
                    utilityTypeIds,
                  });
                },
              });
            },
          });
        };

        const onSubmitRenterContactInformationForm = (formValues, cancelIsSubmitting) => {
          return dispatch(requestUtilityApi, {
            action: SR_API_ACTION_UTILITY_ACTIVATION_GET,
            payload: {
              addressId: selectingAddress.id,
            },
          }).then((activation) => {
            if (
              activation &&
              (!selectingAddress.renterReminderSentUser ||
                (selectingAddress.renterReminderSentUser.phone !== formValues.phone &&
                  selectingAddress.renterReminderSentUser.email !== formValues.email))
            ) {
              cancelIsSubmitting();
              setRenterContactInfoPendingConfirmation(formValues);
            } else {
              return onConfirmRenterContactInformationForm(formValues);
            }
          });
        };

        const onConfirmRenterContactInformationForm = (formValues) => {
          setRenterContactInfoPendingConfirmation(null);
          return dispatch(requestUtilityApi, {
            action: SR_API_ACTION_ADDRESS_START_REMINDERS_FOR_RENTER,
            payload: {
              utilityPartnerAddressId: selectingAddress.partnerAddressId,
              leaseStartDate: formValues.leaseStartDate,
              user: {
                id: formValues.email,
                firstName: formValues.firstName,
                lastName: formValues.lastName,
                email: formValues.email,
                phone: formValues.phone,
              },
            },
            onError(error) {
              global.alert(error.message);
            },
          })
            .then(() => {
              return reloadByAddressId(selectingAddress.id);
            })
            .then(() => {
              setSelectingAddress(null);
            });
        };

        const addressRows = addresses.map((address) => {
          return {
            onClick: (event) => {
              event.currentTarget.tabIndex = -1;
              buildingUnitsReturnFocusRef.current = event.currentTarget;
              setUpdatingAddressId(address.id);
            },
            cells: [
              {
                content: (
                  <SrUtilityProfitAddressDataTableAddressCell
                    address={address}
                    showPartner={showPartner}
                  />
                ),
                style: {
                  maxWidth: '290px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
              },
              {
                content: formatDate(address.createdAt),
                style: {
                  fontWeight: '400',
                },
              },
              {
                content: (
                  <SrText font="greycliff">
                    {filteredUtilityTypes.map(({ id: utilityTypeId }) => {
                      const isRequired = address.requiredTypes?.some((requiredType) => {
                        return requiredType.id === utilityTypeId;
                      });
                      const requiredType = filteredUtilityTypes.find(
                        ({ id }) => id === utilityTypeId,
                      );
                      const displayLabel =
                        utilityTypeId === UTILITY_TYPE_ID_LIABILITY_INSURANCE
                          ? 'Insurance'
                          : requiredType.label;
                      return (
                        <SrText key={utilityTypeId} color={!isRequired ? 'orange300' : ''}>
                          {displayLabel}
                          {utilityTypeId !==
                            filteredUtilityTypes[filteredUtilityTypes.length - 1].id && ', '}
                        </SrText>
                      );
                    })}
                  </SrText>
                ),
                style: {
                  fontWeight: '400',
                },
              },
              {
                content: (
                  <SrUtilityConfigurationSourceMarker
                    provenance={address.utilityConfigurationProvenance}
                    tooltipId={`utility-configuration-source-${address.id}-badge-tooltip`}
                  />
                ),
                style: {
                  fontWeight: '400',
                  width: '112px',
                },
              },
              {
                onClick: (event) => {
                  event.stopPropagation();
                  setSelectingAddress(address);
                },
                content: (
                  <SrBox float="right">
                    {address.renterReminderStartedAt &&
                      (address.renterReminderSentUser ? (
                        <SrTooltip
                          position={SR_TOOLTIP_POSITION_CENTER}
                          bgColor="blue"
                          icon={<span />}
                          tooltipContentStyle={{
                            transform: 'translateX(0)',
                          }}
                          label={
                            <SrText
                              font="greycliff"
                              weight="500"
                              paddingRight="20px"
                              underlineOnHover
                            >
                              Reminders started!
                            </SrText>
                          }
                        >
                          {makeFullName(
                            address.renterReminderSentUser.firstName,
                            address.renterReminderSentUser.lastName,
                          ) +
                            '\n' +
                            (address.renterReminderSentUser?.leaseStartDate
                              ? dateStringMomentWithCurrentTime(
                                  address.renterReminderSentUser.leaseStartDate,
                                ).format('MM/DD/YYYY')
                              : '')}
                        </SrTooltip>
                      ) : (
                        <SrText font="greycliff" weight="500" paddingRight="20px" underlineOnHover>
                          Reminders started!
                        </SrText>
                      ))}

                    {!address.renterReminderStartedAt && (
                      <SrText font="greycliff" weight="700" color="green" paddingRight="20px">
                        {isHomebuyer ? 'Send Reminders' : 'Send renter reminders'}
                      </SrText>
                    )}
                  </SrBox>
                ),
              },
              {
                content: (
                  <SrBox float="right">
                    <SrText font="greycliff" color="blue300" size="16" weight="700">
                      Edit
                    </SrText>
                  </SrBox>
                ),
              },
            ],
          };
        });

        const loadMoreRow =
          dataTableIsFullyLoaded || viewSettings?.fuzzy?.queryString
            ? null
            : {
                onClick: () => (dataTableApiState.inProgress ? null : loadNextPage()),
                cells: [
                  {
                    content: (
                      <div
                        style={{ height: '100%', textAlign: 'center', backgroundColor: '#F5F9FF' }}
                      >
                        <SrText
                          lineHeight="50px"
                          font="greycliff"
                          weight="800"
                          size="16"
                          color="blue300"
                        >
                          View more listings
                        </SrText>
                      </div>
                    ),
                    colSpan: dataTableHeaders.length,
                    noPadding: true,
                  },
                ],
              };

        return (
          <SrBox>
            <SrUtilityProfitAddressesDataTableHeader
              title={title}
              searchInput={searchInput}
              onSearchInputChange={(input) =>
                onSearchInputChange({ loadNextPage, viewSettings, input })
              }
              onAddAddress={() => setNewAddressModalIsOpen(true)}
            />
            <SrBox marginTop="20px">
              <SrDataTableUI
                sticky
                theme={SR_TABLE_THEME_PARTNER_CLEAR}
                dataTableViewSettings={viewSettings}
                headers={dataTableHeaders}
                minWidth="1080px"
                rows={lodashCompact([...addressRows, loadMoreRow])}
              />
              {dataTableApiState.inProgress && (
                <div>
                  <div className="loading-ellipses clearfix" />
                </div>
              )}
            </SrBox>
            {addresses.length === 0 && dataTableIsFullyLoaded && (
              <SrBox backgroundColor="white" display="flex" justifyContent="center" padding="20px">
                <SrText font="greycliff" weight="500" fontSize="16px" fontStyle="italic">
                  {searchInput ? 'No search results.' : 'No listings added yet.'}
                </SrText>
              </SrBox>
            )}
            {updatingAddress && (
              <SrUtilityAddressRequiredTypesModal
                address={updatingAddress}
                initialUtilityTypeIds={updatingAddress.requiredTypes.map(({ id }) => id)}
                utilityPartner={utilityPartner}
                utilityPartnerSlug={utilityPartnerSlug}
                utilityProviders={updatingAddress.utilityProviders}
                utilityTypes={utilityTypes}
                requiredTypes={updatingAddress.requiredTypes}
                onClose={() => setUpdatingAddressId(null)}
                onSubmit={updateRequiredTypes}
                onAddressUpdate={reloadByAddressId}
                tenantLiabilityEnabled={tenantLiabilityEnabled}
                dispatch={dispatch}
              />
            )}
            {buildingUnitsConfirmation && (
              <SrUtilityBuildingUnitsConfirmationModal
                buildingDisplay={buildingUnitsConfirmation.buildingDisplay}
                errorMessage={buildingUnitsUpdateError}
                excludedSiblingAddressIds={
                  buildingUnitsConfirmation.excludedSiblingAddressIds
                }
                isApplying={buildingUnitsAreUpdating}
                onApply={applyBuildingUnitSettings}
                onClose={closeBuildingUnitsConfirmation}
                onToggleSiblingUnit={toggleBuildingUnitSelection}
                previewFailed={buildingUnitsConfirmation.previewFailed}
                previewIsLoading={buildingUnitsConfirmation.previewIsLoading}
                returnFocusElement={buildingUnitsReturnFocusRef.current}
                siblingUnits={buildingUnitsConfirmation.siblingUnits}
                sourceUnit={buildingUnitsConfirmation.sourceUnit}
                targetSiblingUnits={buildingUnitsConfirmation.targetSiblingUnits}
                utilityTypeIds={buildingUnitsConfirmation.utilityTypeIds}
                utilityTypes={utilityTypes}
              />
            )}
            {newAddressModalIsOpen && (
              <SrUtilityProfitNewAddressModal
                utilityPartnerId={utilityPartnerId}
                utilityPartnerList={utilityPartnerList}
                afterSubmit={(address) => {
                  if (address) {
                    reloadByAddressId(address.id);
                  }
                }}
                onSuccessModalClose={() => setNewAddressModalIsOpen(false)}
                onClose={() => setNewAddressModalIsOpen(false)}
              />
            )}
            {selectingAddress && (
              <SrUtilityProfitRenterContactInformationModal
                address={selectingAddress}
                isHomebuyer={isHomebuyer}
                onClose={() => setSelectingAddress(null)}
                onSubmit={onSubmitRenterContactInformationForm}
                initialValues={selectingAddress.renterReminderSentUser}
              />
            )}
            {renterContactInfoPendingConfirmation && (
              <SrSimpleMessageModal
                iconUrl="/images/question-mark.svg"
                maxIconHeight="50px"
                maxIconWidth="50px"
                contentContainerClassName="utilityProviderContact"
                iconBgColor="#FFFBEB"
                headline={
                  isHomebuyer
                    ? 'This utility activation has already been started, are you sure you want to restart it with the new client?'
                    : 'This utility activation has already been started, are you sure you want to restart it with the new tenant?'
                }
                underline
                maxWidth={400}
                message={
                  isHomebuyer
                    ? 'By restarting, the current utility confirmations will disappear and the new client will need to confirm them all again.'
                    : 'By restarting, the current utility confirmations will disappear and the new tenant will need to confirm them all again.'
                }
                theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
                srMultiButtons={[
                  {
                    buttonText: 'Restart Utility Activations',
                    onButtonClick: () => {
                      onConfirmRenterContactInformationForm(renterContactInfoPendingConfirmation);
                    },
                    buttonTheme: SR_BUTTON_THEME_ADOBE_YELLOW,
                  },
                  {
                    buttonText: 'Cancel',
                    onButtonClick: () => setRenterContactInfoPendingConfirmation(null),
                    buttonTheme: SR_BUTTON_THEME_ADOBE_GREY,
                  },
                ]}
              />
            )}
          </SrBox>
        );
      }}
    </SrDataTableConnected>
  );
};

SrUtilityProfitAddressesDataTable.propTypes = {
  title: PropTypes.string,
  showPartner: PropTypes.bool,
  utilityPartner: PropTypes.object,
  utilityPartnerSlug: PropTypes.string,
  utilityPartnerList: PropTypes.array,
  utilityTypes: PropTypes.array.isRequired,
  tenantLiabilityEnabled: PropTypes.bool,
  backfillTriggeredAt: PropTypes.number,
};

SrUtilityProfitAddressesDataTable.displayName = 'SrUtilityProfitAddressesDataTable';

export default SrUtilityProfitAddressesDataTable;
